import { DraggableProvided } from '@hello-pangea/dnd'
import { MinusCircle, PlusCircle } from 'lucide-react'
import React from 'react'

import {
  getBackgroundColorByTableRowType,
  UpdateQuoteTableRowTypeEnum,
} from './utils/utils'
import { Button } from '../../../../../../../../../shadcn/components/ui/button'
import {
  TableCell,
  TableRow,
} from '../../../../../../../../../shadcn/components/ui/table'
import { cn } from '../../../../../../../../../shadcn/utils'
import Icon, { IconName } from '../../../../../../../../atoms/icons/icon'
import { VAT_STATUS } from '../../../../../../../../contexts'
import { useVatStatus } from '../../../../../../../../contexts/vat-status-context/useVatStatus'

export interface IQuoteSectionBodyTableRowProps {
  id: string
  label: string
  description: string
  unit: string
  commission: string
  quantity: number
  unitPrice: string
  totalPriceHT: string
  vatRate: string
  totalPriceTTC: string
  unitPriceWithDiscount?: string
  totalPriceHTWithDiscount?: string
  totalPriceTTCWithDiscount?: string
  type?: UpdateQuoteTableRowTypeEnum
  actions: {
    icon: { name: IconName; size: number }
    onClick: ({
      clientPricingItemId,
      vatStatus,
    }: {
      clientPricingItemId?: string
      vatStatus?: VAT_STATUS
    }) => void
  }[]
  draggableProvided?: DraggableProvided
  isDragDisabled?: boolean
}

const QuoteSectionBodyTableRow = ({
  id,
  commission,
  description,
  label,
  quantity,
  totalPriceHT,
  totalPriceTTC,
  unit,
  unitPrice,
  vatRate,
  totalPriceHTWithDiscount,
  totalPriceTTCWithDiscount,
  unitPriceWithDiscount,
  type = UpdateQuoteTableRowTypeEnum.DEFAULT,
  actions = [],
  draggableProvided,
  isDragDisabled,
}: IQuoteSectionBodyTableRowProps) => {
  const { vatStatus } = useVatStatus()
  const hasDiscount = totalPriceHTWithDiscount !== totalPriceHT

  return (
    <TableRow
      ref={draggableProvided ? draggableProvided.innerRef : undefined}
      {...(draggableProvided ? draggableProvided.draggableProps : {})}
      {...(draggableProvided ? draggableProvided.dragHandleProps : {})}
      className={cn(
        `border-b-[1px] border-grey-400 px-2 py-2.5 ${type === UpdateQuoteTableRowTypeEnum.REMOVED && 'line-through'}`,
        getBackgroundColorByTableRowType(type),
      )}
    >
      {!isDragDisabled && (
        <TableCell>
          <Icon size={20} name='drag-drop-dots' />
        </TableCell>
      )}
      <TableCell className='h-full'>
        <div className='flex items-center gap-2'>
          {type !== UpdateQuoteTableRowTypeEnum.DEFAULT && (
            <div>
              <IconAdjustment type={type} />
            </div>
          )}
          <div>
            <span>{label}</span>
            <br />
            <span className='text-grey-800'>{description}</span>
          </div>
        </div>
      </TableCell>
      <TableCell className='whitespace-nowrap'>{unit}</TableCell>

      <TableCell>
        <CustomCell
          value={commission}
          newValue={hasDiscount ? commission : undefined}
        />
      </TableCell>
      <TableCell>
        <CustomCell
          value={quantity}
          newValue={hasDiscount ? quantity : undefined}
        />
      </TableCell>
      <TableCell>
        <CustomCell
          value={unitPrice}
          newValue={hasDiscount ? unitPriceWithDiscount : undefined}
        />
      </TableCell>
      <TableCell>
        <CustomCell
          value={totalPriceHT}
          newValue={hasDiscount ? totalPriceHTWithDiscount : undefined}
        />
      </TableCell>
      <TableCell className='whitespace-nowrap'>
        <CustomCell
          value={vatRate}
          newValue={hasDiscount ? vatRate : undefined}
        />
      </TableCell>
      <TableCell>
        <CustomCell
          value={totalPriceTTC}
          newValue={hasDiscount ? totalPriceTTCWithDiscount : undefined}
        />
      </TableCell>
      {actions.length > 0 && (
        <TableCell>
          <div className='flex items-center gap-1'>
            {actions.map((action, i) => (
              <Button
                key={i}
                size='icon'
                variant='outline'
                className='text-grey-900 flex h-6 w-6 items-center justify-center rounded-md border'
                onClick={() =>
                  action.onClick({ clientPricingItemId: id, vatStatus })
                }
              >
                <Icon
                  name={action.icon.name}
                  size={action.icon.size}
                  className='cursor-pointer'
                />
              </Button>
            ))}
          </div>
        </TableCell>
      )}
    </TableRow>
  )
}

export default QuoteSectionBodyTableRow

const CustomCell = ({
  value,
  newValue,
}: {
  value: string | number
  newValue?: string | number
}) => {
  if (newValue) {
    return (
      <div className='whitespace-nowrap'>
        <span className='line-through'>{value}</span>
        <br />
        <span className='text-[#0CBC8B]'>{newValue}</span>
      </div>
    )
  }
  return <span className='whitespace-nowrap'>{value}</span>
}

const IconAdjustment = ({ type }: { type: UpdateQuoteTableRowTypeEnum }) => {
  if (type === UpdateQuoteTableRowTypeEnum.ADDED) {
    return <PlusCircle size={18} color='white' fill='#0CBC8B' />
  } else if (type === UpdateQuoteTableRowTypeEnum.REMOVED) {
    return <MinusCircle size={18} color='white' fill='#F87171' />
  } else {
    return null
  }
}
